import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Image, VideoCamera, Sparkle, MagnifyingGlass, Check, PencilSimple, ArrowsClockwise, Lightning, Monitor, DeviceMobile } from '@phosphor-icons/react';
import { EnhancePromptButtons } from './EnhancePromptButtons';

export const VisualPreview = ({ scene, project, onUpdateScene, onGenerateImage, onSearchStock, saving }) => {
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [promptValue, setPromptValue] = useState(scene.visual_prompt);
  const [videoPromptValue, setVideoPromptValue] = useState(scene.video_prompt || '');
  const [editingVideoPrompt, setEditingVideoPrompt] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState(project?.aspect_ratio || '16:9');

  const handleSavePrompt = async () => {
    await onUpdateScene(scene.id, { visual_prompt: promptValue });
    setEditingPrompt(false);
  };

  const handleSaveVideoPrompt = async () => {
    await onUpdateScene(scene.id, { video_prompt: videoPromptValue });
    setEditingVideoPrompt(false);
  };

  const handleGenerate = async (aspectRatio = selectedAspectRatio) => {
    setGenerating(true);
    await onGenerateImage(scene.id, promptValue || scene.visual_prompt, aspectRatio);
    setGenerating(false);
  };

  const handleApprove = async () => {
    await onUpdateScene(scene.id, { status: scene.status === 'approved' ? 'draft' : 'approved' });
  };

  const handleEnhancePrompt = (style) => {
    const enhancements = {
      cinematic: ', cinematic lighting, dramatic composition, 35mm film look, shallow depth of field',
      realistic: ', photorealistic, natural lighting, 8K resolution, hyperdetailed',
      dramatic: ', high contrast, moody atmosphere, chiaroscuro lighting, film noir style'
    };
    setPromptValue(prev => prev + (enhancements[style] || ''));
  };

  return (
    <div className="h-full flex gap-6">
      {/* Visual Preview Area */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-[#FF5D01] font-mono font-bold">Scene {scene.scene_number}</span>
            <span className={`px-2 py-0.5 rounded text-xs ${scene.status === 'approved' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#262626] text-[#A1A1AA]'}`}>
              {scene.status}
            </span>
            <div className="flex items-center gap-1.5 text-[#A1A1AA] text-sm">
              {scene.media_type === 'video' ? <VideoCamera size={16} /> : <Image size={16} />}
              {scene.duration}s
            </div>
          </div>
          <Button
            variant={scene.status === 'approved' ? 'default' : 'outline'}
            size="sm"
            onClick={handleApprove}
            className={scene.status === 'approved' 
              ? 'bg-[#10B981] hover:bg-[#059669] text-white gap-2' 
              : 'border-[#333333] text-[#A1A1AA] hover:text-white hover:bg-[#262626] gap-2'}
            data-testid="approve-scene-btn"
          >
            <Check size={16} weight="bold" />
            {scene.status === 'approved' ? 'Approved' : 'Approve'}
          </Button>
        </div>

        {/* Image Preview */}
        <div className="flex-1 bg-[#171717] border border-[#333333] rounded-lg overflow-hidden flex items-center justify-center min-h-[300px]">
          {scene.asset_url ? (
            <img 
              src={scene.asset_url} 
              alt={`Scene ${scene.scene_number}`} 
              className="max-w-full max-h-full object-contain"
              data-testid="scene-preview-image"
            />
          ) : (
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#262626] rounded-full flex items-center justify-center mx-auto mb-4">
                <Image size={32} className="text-[#333333]" />
              </div>
              <p className="text-[#A1A1AA] mb-4">No visual asset yet</p>
              
              {/* Aspect Ratio Selector */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-xs text-[#A1A1AA]">Aspect Ratio:</span>
                <div className="flex gap-1 bg-[#262626] p-1 rounded-lg">
                  <button
                    onClick={() => setSelectedAspectRatio('16:9')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      selectedAspectRatio === '16:9' 
                        ? 'bg-[#FF5D01] text-white' 
                        : 'text-[#A1A1AA] hover:text-white'
                    }`}
                    data-testid="aspect-ratio-16-9-btn"
                  >
                    <Monitor size={14} />
                    16:9
                  </button>
                  <button
                    onClick={() => setSelectedAspectRatio('9:16')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      selectedAspectRatio === '9:16' 
                        ? 'bg-[#FF5D01] text-white' 
                        : 'text-[#A1A1AA] hover:text-white'
                    }`}
                    data-testid="aspect-ratio-9-16-btn"
                  >
                    <DeviceMobile size={14} />
                    9:16
                  </button>
                </div>
              </div>
              
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={() => handleGenerate()}
                  disabled={generating || saving}
                  className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
                  data-testid="generate-image-btn"
                >
                  <Sparkle size={18} weight="fill" />
                  {generating ? 'Generating...' : `Generate ${selectedAspectRatio} Image`}
                </Button>
                <Button
                  variant="outline"
                  onClick={onSearchStock}
                  className="border-[#333333] text-white hover:bg-[#262626] gap-2"
                  data-testid="search-stock-btn"
                >
                  <MagnifyingGlass size={18} />
                  Search Stock
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Asset Info */}
        {scene.asset_url && (
          <div className="mt-4 p-4 bg-[#171717] border border-[#333333] rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-white font-medium">{scene.asset_source || 'Unknown source'}</div>
                <div className="text-xs text-[#A1A1AA]">{scene.license_type || 'License not specified'}</div>
                {scene.attribution && <div className="text-xs text-[#A1A1AA] mt-1">{scene.attribution}</div>}
              </div>
              <div className="flex items-center gap-2">
                {/* Aspect ratio toggle for regeneration */}
                <div className="flex gap-0.5 bg-[#0a0a0a] p-0.5 rounded">
                  <button
                    onClick={() => setSelectedAspectRatio('16:9')}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                      selectedAspectRatio === '16:9' ? 'bg-[#FF5D01] text-white' : 'text-[#A1A1AA] hover:text-white'
                    }`}
                    title="16:9 Landscape"
                  >
                    <Monitor size={12} />
                  </button>
                  <button
                    onClick={() => setSelectedAspectRatio('9:16')}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                      selectedAspectRatio === '9:16' ? 'bg-[#FF5D01] text-white' : 'text-[#A1A1AA] hover:text-white'
                    }`}
                    title="9:16 Portrait/Vertical"
                  >
                    <DeviceMobile size={12} />
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerate()}
                  disabled={generating || saving}
                  className="border-[#333333] text-[#A1A1AA] hover:text-white hover:bg-[#262626] gap-1"
                  data-testid="regenerate-image-btn"
                >
                  <ArrowsClockwise size={16} />
                  {selectedAspectRatio}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSearchStock}
                  className="border-[#333333] text-[#A1A1AA] hover:text-white hover:bg-[#262626] gap-1"
                  data-testid="swap-to-stock-btn"
                >
                  <MagnifyingGlass size={16} />
                  Swap
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Panel - Prompts & Script */}
      <div className="w-96 flex flex-col gap-4">
        {/* Script Excerpt */}
        <div className="bg-[#171717] border border-[#333333] rounded-lg p-4">
          <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">Script Excerpt</Label>
          <p className="text-sm text-white leading-relaxed max-h-32 overflow-y-auto">{scene.script_excerpt}</p>
        </div>

        {/* Prompts */}
        <Tabs defaultValue="image" className="flex-1">
          <TabsList className="bg-[#171717] border border-[#333333] p-1 w-full">
            <TabsTrigger value="image" className="flex-1 data-[state=active]:bg-[#262626]" data-testid="image-prompt-tab">
              Image Prompt
            </TabsTrigger>
            <TabsTrigger value="video" className="flex-1 data-[state=active]:bg-[#262626]" data-testid="video-prompt-tab">
              Video Prompt
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="image" className="mt-3">
            <div className="bg-[#171717] border border-[#333333] rounded-lg p-4">
              {editingPrompt ? (
                <>
                  <Textarea
                    value={promptValue}
                    onChange={(e) => setPromptValue(e.target.value)}
                    className="bg-[#0a0a0a] border-[#333333] text-white min-h-[120px] mb-3"
                    data-testid="visual-prompt-input"
                  />
                  <div className="flex gap-2 mb-3">
                    <EnhancePromptButtons 
                      sceneId={scene.id} 
                      currentPrompt={promptValue}
                      onEnhanced={(updatedScene) => {
                        setPromptValue(updatedScene.visual_prompt);
                      }}
                      compact={true}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSavePrompt} disabled={saving} className="bg-[#FF5D01] hover:bg-[#e65400]" data-testid="save-prompt-btn">
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingPrompt(false); setPromptValue(scene.visual_prompt); }} className="text-[#A1A1AA]">
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-white mb-3">{scene.visual_prompt}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingPrompt(true)}
                    className="text-[#A1A1AA] hover:text-white gap-1"
                    data-testid="edit-prompt-btn"
                  >
                    <PencilSimple size={14} />
                    Edit Prompt
                  </Button>
                </>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="video" className="mt-3">
            <div className="bg-[#171717] border border-[#333333] rounded-lg p-4">
              <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">Text-to-Video Prompt</Label>
              <p className="text-xs text-[#A1A1AA] mb-3">Use this prompt with external video generators (Sora, Runway, etc.)</p>
              {editingVideoPrompt ? (
                <>
                  <Textarea
                    value={videoPromptValue}
                    onChange={(e) => setVideoPromptValue(e.target.value)}
                    className="bg-[#0a0a0a] border-[#333333] text-white min-h-[100px] mb-3"
                    data-testid="video-prompt-input"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveVideoPrompt} disabled={saving} className="bg-[#FF5D01] hover:bg-[#e65400]" data-testid="save-video-prompt-btn">
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingVideoPrompt(false); setVideoPromptValue(scene.video_prompt || ''); }} className="text-[#A1A1AA]">
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-white mb-3 font-mono text-xs bg-[#0a0a0a] p-3 rounded">{scene.video_prompt || 'No video prompt generated'}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingVideoPrompt(true)}
                    className="text-[#A1A1AA] hover:text-white gap-1"
                    data-testid="edit-video-prompt-btn"
                  >
                    <PencilSimple size={14} />
                    Edit Video Prompt
                  </Button>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Duration */}
        <div className="bg-[#171717] border border-[#333333] rounded-lg p-4">
          <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">Duration (seconds)</Label>
          <Input
            type="number"
            value={scene.duration}
            onChange={(e) => onUpdateScene(scene.id, { duration: parseInt(e.target.value) || 5 })}
            min={1}
            max={60}
            className="bg-[#0a0a0a] border-[#333333] text-white w-24"
            data-testid="scene-duration-input"
          />
        </div>
      </div>
    </div>
  );
};
